import React, { useState } from 'react';
import { Modal, Form, Button, Badge } from 'react-bootstrap';
import Select from './common/Select';
import {
  useGetMarketingEmailLists,
} from '../api/generated/arrayscapeGamingDashboardAPI';

// Type for list membership with optional id and name
export interface ListMembership {
  id?: string;
  name?: string;
}

interface ListSelectorProps {
  /**
   * Selected lists - array of { id, name } objects.
   * Lists with id are existing, lists with only name are new.
   */
  selectedLists: ListMembership[];
  /**
   * Callback when selection changes
   */
  onChange: (lists: ListMembership[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  /**
   * Whether to allow creating new lists. Defaults to true.
   */
  allowCreate?: boolean;
}

/**
 * Reusable component for selecting email lists with the ability to create new lists.
 * New lists are not created immediately - they are passed as { name } without id,
 * and the backend will create them when the contact is saved.
 */
export const ListSelector: React.FC<ListSelectorProps> = ({
  selectedLists,
  onChange,
  placeholder = 'Select lists...',
  isDisabled = false,
  allowCreate = true,
}) => {
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [createListError, setCreateListError] = useState('');
  const [selectInputValue, setSelectInputValue] = useState('');

  const { data: emailLists } = useGetMarketingEmailLists();

  const handleCloseCreateModal = () => {
    setShowCreateListModal(false);
    setNewListName('');
    setCreateListError('');
  };

  // Try to add a list by name. Returns { success, error? }
  const tryAddListByName = (name: string): { success: boolean; error?: string } => {
    const trimmedName = name.trim();
    if (!trimmedName) return { success: false };

    const trimmedNameLower = trimmedName.toLowerCase();

    // Check if this name already exists in selected lists (case-insensitive)
    const alreadySelected = selectedLists.some(l => l.name?.toLowerCase() === trimmedNameLower);
    if (alreadySelected) {
      return { success: false, error: `"${trimmedName}" is already selected.` };
    }

    // Check if a list with this name already exists (case-insensitive)
    const existingList = (emailLists || []).find(l => l.name.toLowerCase() === trimmedNameLower);
    if (existingList) {
      return { success: false, error: `A list named "${existingList.name}" already exists. Select it from the dropdown instead.` };
    }

    // Add as pending (no id, only name)
    onChange([...selectedLists, { name: trimmedName }]);
    return { success: true };
  };

  const handleCreateList = () => {
    const result = tryAddListByName(newListName);
    if (result.error) {
      setCreateListError(result.error);
      return;
    }
    if (result.success) {
      handleCloseCreateModal();
    }
  };

  // Option type
  interface ListOption {
    value: string;
    label: string;
    listId?: string;
    listName?: string;
    __isNew__: boolean;
    __isPending__: boolean;
  }

  // Build options from existing lists, sorted alphabetically by name
  const existingOptions: ListOption[] = (emailLists || [])
    .map(list => ({
      value: list.id,
      label: list.name,
      listId: list.id,
      listName: list.name,
      __isNew__: false,
      __isPending__: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Build options for pending (new) lists that don't have an id yet, sorted alphabetically
  const pendingOptions: ListOption[] = selectedLists
    .filter(l => !l.id && l.name)
    .map(l => ({
      value: `__pending__${l.name}`,
      label: l.name!,
      listId: undefined,
      listName: l.name!,
      __isNew__: false,
      __isPending__: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const createNewOption: ListOption = {
    value: '__create_new__',
    label: '+ Create New List',
    listId: undefined,
    listName: undefined,
    __isNew__: true,
    __isPending__: false,
  };

  const allOptions: ListOption[] = allowCreate
    ? [...existingOptions, ...pendingOptions, createNewOption]
    : [...existingOptions, ...pendingOptions];

  // Build current value from selectedLists, preserving insertion order
  const currentValue: ListOption[] = selectedLists.map(l => {
    if (l.id) {
      // Find in existing options
      const opt = existingOptions.find(o => o.value === l.id);
      if (opt) return opt;
    }
    if (l.name) {
      // Find in pending options
      const opt = pendingOptions.find(o => o.listName === l.name);
      if (opt) return opt;
    }
    return null;
  }).filter((opt): opt is ListOption => opt !== null);

  const handleSelectChange = (selectedOptions: any) => {
    const selected = selectedOptions as Array<{
      value: string;
      label: string;
      listId?: string;
      listName?: string;
      __isNew__?: boolean;
      __isPending__?: boolean;
    }> || [];

    // Check if "Create New List" was selected
    if (selected.find(opt => opt.value === '__create_new__')) {
      setShowCreateListModal(true);
      return;
    }

    // Convert selections to ListMembership format
    const lists: ListMembership[] = selected
      .filter(opt => !opt.__isNew__)
      .map(opt => {
        if (opt.__isPending__) {
          return { name: opt.listName };
        }
        return { id: opt.listId, name: opt.listName };
      });

    onChange(lists);
  };

  return (
    <>
      <Select
        isMulti
        closeMenuOnSelect={false}
        isDisabled={isDisabled}
        value={currentValue}
        onChange={handleSelectChange}
        options={allOptions}
        placeholder={placeholder}
        isClearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        inputValue={selectInputValue}
        onInputChange={(value, action) => {
          if (action.action === 'input-change') {
            setSelectInputValue(value);
          } else if (action.action === 'set-value' || action.action === 'input-blur') {
            // Clear input when a value is selected or input loses focus
            setSelectInputValue('');
          }
        }}
        onKeyDown={(e) => {
          if (allowCreate && e.key === 'Enter' && selectInputValue.trim()) {
            const trimmedInput = selectInputValue.trim().toLowerCase();
            // Check if input matches any existing or pending list
            const matchesExisting = (emailLists || []).some(
              l => l.name.toLowerCase() === trimmedInput
            );
            const matchesPending = selectedLists.some(
              l => !l.id && l.name?.toLowerCase() === trimmedInput
            );

            if (!matchesExisting && !matchesPending) {
              // Input doesn't match any list - create it as pending
              e.preventDefault();
              const result = tryAddListByName(selectInputValue);
              if (result.success) {
                setSelectInputValue('');
              }
              // If there's an error (e.g., already selected), we could show a toast,
              // but for now we just don't add it and let them see the dropdown
            }
          }
        }}
        filterOption={(option, inputValue) => {
          // Always show "Create New List" when allowed
          if (option.data?.__isNew__) {
            return allowCreate;
          }
          return option.label.toLowerCase().includes(inputValue.toLowerCase());
        }}
        formatOptionLabel={(option: any) => {
          if (option.__isNew__) {
            return (
              <div style={{ fontWeight: 600, color: '#28a745' }}>
                <i className="bi bi-plus-circle me-2"></i>
                {option.label}
              </div>
            );
          }
          if (option.__isPending__) {
            return (
              <div>
                {option.label}
                <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7em' }}>
                  New
                </Badge>
              </div>
            );
          }
          return <div>{option.label}</div>;
        }}
        styles={{
          menuList: (base) => ({
            ...base,
            display: 'flex',
            flexDirection: 'column',
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.data?.__isNew__
              ? (state.isFocused ? 'var(--bs-tertiary-bg)' : 'var(--bs-body-bg)')
              : state.data?.__isPending__
                ? (state.isFocused ? 'var(--bs-warning-bg-subtle)' : 'var(--bs-warning-bg-subtle)')
                : (state.isFocused ? 'var(--bs-tertiary-bg)' : 'var(--bs-body-bg)'),
            borderBottom: state.data?.__isNew__ ? '1px solid var(--bs-border-color)' : undefined,
            order: state.data?.__isNew__ ? -1 : 0,
          }),
          multiValue: (base, state) => ({
            ...base,
            backgroundColor: state.data?.__isPending__ ? 'var(--app-highlight-bg)' : base.backgroundColor,
          }),
        }}
      />

      {/* Create New List Modal */}
      <Modal show={showCreateListModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Email List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>List Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., VIP Customers"
                value={newListName}
                onChange={(e) => {
                  setNewListName(e.target.value);
                  setCreateListError('');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateList();
                  }
                }}
                isInvalid={!!createListError}
                autoFocus
              />
              {createListError && (
                <Form.Control.Feedback type="invalid">
                  {createListError}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Form>
          {!createListError && (
            <Form.Text className="text-muted mt-2">
              This list will be created when you save the contact.
            </Form.Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateList}
            disabled={!newListName.trim()}
          >
            Add List
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ListSelector;
